<script setup lang="ts">
import { computed, ref } from 'vue'
import VueApexCharts from 'vue3-apexcharts'
import type { IForecastVsActual } from '@/interfaces/entities/management'

type Mode = 'mensal' | 'acumulado'
const props = withDefaults(defineProps<{ data: IForecastVsActual | null; defaultMode?: Mode }>(), {
  defaultMode: 'mensal',
})

const mode = ref<Mode>(props.defaultMode)

const points = computed(() => {
  if (!props.data) return []
  return mode.value === 'mensal' ? props.data.months : props.data.accumulated
})

const categories = computed(() => points.value.map((p) => p.label ?? p.month))

const series = computed(() => [
  { name: 'Previsto', data: points.value.map((p) => Number(p.previsto ?? 0)) },
  { name: 'Realizado', data: points.value.map((p) => Number(p.realizado ?? 0)) },
])

const chartType = computed(() => (mode.value === 'mensal' ? 'bar' : 'line'))

// Rótulo compacto para caber em cada coluna/ponto (ex.: 240000 → "240k").
const compact = (v: number) => {
  const n = Number(v)
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(1).replace('.', ',')}M`
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(1).replace('.', ',')}k`
  return n.toLocaleString('pt-BR', { maximumFractionDigits: 0 })
}

const options = computed(() => ({
  chart: { type: chartType.value, stacked: false, toolbar: { show: false }, fontFamily: 'inherit' },
  colors: ['#3056D3', '#10B981'], // previsto azul, realizado verde
  plotOptions: { bar: { columnWidth: '60%', borderRadius: 2, dataLabels: { position: 'top' } } },
  stroke: mode.value === 'acumulado' ? { width: 3, curve: 'smooth' } : { width: 0 },
  // Rótulo do valor em cada coluna/ponto.
  dataLabels: {
    enabled: true,
    formatter: (v: number) => compact(v),
    offsetY: mode.value === 'mensal' ? -16 : -8,
    style: { fontSize: '9px', colors: ['#637381'] },
    background: { enabled: false },
  },
  markers: { size: mode.value === 'acumulado' ? 4 : 0 },
  legend: { position: 'top', horizontalAlign: 'left' },
  xaxis: { categories: categories.value },
  yaxis: {
    labels: { formatter: (v: number) => `${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}` },
  },
  tooltip: {
    y: { formatter: (v: number) => `${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} kWh` },
  },
}))

const totals = computed(() => props.data?.totals ?? { previsto: null, realizado: null })
const fmt = (v: number | null) => (v == null ? '—' : `${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} kWh`)
</script>

<template>
  <div class="border border-stroke bg-white p-4">
    <div class="mb-3 flex items-center justify-between">
      <h4 class="text-xs font-semibold uppercase tracking-wide text-slate-400">Previsto × Realizado · consumo (kWh)</h4>
      <div class="flex border border-stroke">
        <button
          v-for="opt in (['mensal', 'acumulado'] as Mode[])"
          :key="opt"
          type="button"
          class="px-3 py-1 text-xs font-medium capitalize"
          :class="mode === opt ? 'bg-primary text-white' : 'text-slate-600'"
          @click="mode = opt">
          {{ opt }}
        </button>
      </div>
    </div>

    <div v-if="!data || !points.length" class="flex min-h-[260px] items-center justify-center text-sm text-slate-400">
      Sem dados de previsto/realizado para o período.
    </div>
    <template v-else>
      <VueApexCharts :type="chartType" height="300" :options="options" :series="series" />
      <div class="mt-2 flex flex-wrap gap-6 text-sm">
        <span><span class="text-slate-400">Previsto acumulado:</span> <strong style="color: #3056d3">{{ fmt(totals.previsto) }}</strong></span>
        <span><span class="text-slate-400">Realizado acumulado:</span> <strong style="color: #10b981">{{ fmt(totals.realizado) }}</strong></span>
      </div>
    </template>
  </div>
</template>
